import { useState } from 'react'
import { toast } from '../../lib/toast'
import styles from './GuideSelect.module.css'
import step1 from './images/step1GPT.png';
import step2 from './images/step2GPT.png';
import step3 from './images/step3GPT.png';
import { Copy } from 'lucide-react';
import ImageModal from '../../ui/imageModal/ImageModal';

export default function ConnectGPT() {
  const [mcpServerUrl] = useState(`${import.meta.env.VITE_API_OAUTH_BASE_URL}/mcp`)
  const [modalImg, setModalImg] = useState<string | null>(null);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
    toast.success(`${label} copied to clipboard!`)
  }

  return (
    <div className={styles.container}>

      <div className={styles.header}>
        <h1>Connect ChatGPT to MCP</h1>
        <p>Follow these visual steps to connect your tools to ChatGPT</p>
      </div>

      <div className={styles.steps}>
        {/* Step 1: Open ChatGPT Settings & Go to Apps */}
        <div className={styles.step}>
          <div className={styles.stepNumber}>1</div>
          <div className={styles.stepContent}>
            <h2>Open ChatGPT Settings and Go to Apps</h2>
            <p>
              Open ChatGPT and click on your profile, then go to <strong>Settings</strong>.<br />
              In the Settings menu, click on <strong>Apps</strong>, then click <strong>Add more</strong>.
            </p>
            <div className={styles.imageContainer}>
              <img
                src={step1}
                alt="Open ChatGPT Settings - Apps"
                className={styles.stepImage}
                style={{ cursor: 'zoom-in' }}
                onClick={() => setModalImg(step1)}
                onError={(e) => {
                  e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="400"%3E%3Crect width="800" height="400" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="24" fill="%23666"%3EStep 1: Open Settings%3C/text%3E%3C/svg%3E'
                }}
              />
            </div>
          </div>
        </div>

        {/* Step 2: Configure New App */}
        <div className={styles.step}>
          <div className={styles.stepNumber}>2</div>
          <div className={styles.stepContent}>
            <h2>Configure Your MCP App</h2>
            <p>In the <strong>New App</strong> form, set the name and paste your MCP Server URL.</p>
            <p>Set <strong>Authentication</strong> to <code>OAuth</code>, then check <em>"I understand and want to continue"</em> and click <strong>Create</strong>.</p>

            <div className={styles.configForm}>
              <div className={styles.formGroup}>
                <label>App Name:</label>
                <div className={styles.inputGroup}>
                  <input
                    type="text"
                    value={'MCP Studio'}
                    disabled={true}
                    className={styles.input}
                  />
                  <button
                    onClick={() => copyToClipboard('MCP Studio', 'App Name')}
                    className={styles.copyBtn}
                  ><Copy size={16} /></button>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>MCP Server URL:</label>
                <div className={styles.inputGroup}>
                  <input
                    type="text"
                    value={mcpServerUrl || 'https://your-mcp-server.com/mcp'}
                    className={styles.input}
                    disabled={true}
                  />
                  <button
                    onClick={() => copyToClipboard(mcpServerUrl || 'https://your-mcp-server.com/mcp', 'MCP Server URL')}
                    className={styles.copyBtn}
                  ><Copy size={16} /></button>
                </div>
              </div>
            </div>

            <div className={styles.imageContainer}>
              <img
                src={step2}
                alt="Configure New App"
                className={styles.stepImage}
                style={{ cursor: 'zoom-in' }}
                onClick={() => setModalImg(step2)}
                onError={(e) => {
                  e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="400"%3E%3Crect width="800" height="400" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="24" fill="%23666"%3EStep 2: Configure App%3C/text%3E%3C/svg%3E'
                }}
              />
            </div>
          </div>
        </div>

        {/* Step 3: Connected */}
        <div className={styles.step}>
          <div className={styles.stepNumber}>3</div>
          <div className={styles.stepContent}>
            <h2>Login and Authorize</h2>
            <p>You will be prompted to log in and authorize with your MCP username and password.</p>
            <p>Use the same credentials you use to log in to this system.<br />
              Once authorized, you will see <strong>MCP Studio is now connected</strong>.
            </p>
            <div className={styles.imageContainer}>
              <img
                src={step3}
                alt="MCP Studio Connected"
                className={styles.stepImage}
                style={{ cursor: 'zoom-in' }}
                onClick={() => setModalImg(step3)}
                onError={(e) => {
                  e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="400"%3E%3Crect width="800" height="400" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="24" fill="%23666"%3EStep 3: Connected%3C/text%3E%3C/svg%3E'
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className={styles.successCard}>
        <h2>🎉 You're All Set!</h2>
        <p>ChatGPT can now use your tools through the MCP connector. Start chatting and let ChatGPT help you with operations!</p>
      </div>

      <div className={styles.troubleshooting}>
        <h2>Need Help?</h2>
        <div className={styles.troubleItem}>
          <h3>❌ ChatGPT doesn't see my tools</h3>
          <ul>
            <li>Verify the MCP server URL is correct and accessible</li>
            <li>Ensure your tools are enabled in the Tools section of this app</li>
            <li>Restart ChatGPT completely</li>
          </ul>
        </div>
        <div className={styles.troubleItem}>
          <h3>⚠️ Tool execution fails</h3>
          <ul>
            <li>Test your connection in the Connections section</li>
            <li>Verify your credentials are correct</li>
            <li>Check that CSRF tokens are properly configured</li>
          </ul>
        </div>
      </div>

      {modalImg && (
        <ImageModal src={modalImg} alt="Step" onClose={() => setModalImg(null)} />
      )}
    </div>
  )
}
